import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { TrendingUp, DollarSign, Calendar, Percent, Calculator } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const ROICalculator = () => {
    const [inputs, setInputs] = useState({
        purchasePrice: 150000,
        downPayment: 30000,
        interestRate: 7.5,
        loanTerm: 20,
        monthlyRent: 800,
        propertyTax: 150,
        insurance: 80,
        maintenance: 100,
        management: 10,
        appreciation: 3,
        vacancy: 5
    });

    const [results, setResults] = useState(null);

    const handleFocus = (e) => e.target.select();

    const calculate = () => {
        const loanAmount = inputs.purchasePrice - inputs.downPayment;
        const monthlyRate = inputs.interestRate / 100 / 12;
        const numberOfPayments = inputs.loanTerm * 12;

        // Monthly mortgage payment
        const monthlyMortgage = loanAmount *
            (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) /
            (Math.pow(1 + monthlyRate, numberOfPayments) - 1);

        // Monthly expenses
        const managementFee = inputs.monthlyRent * (inputs.management / 100);
        const totalMonthlyExpenses = inputs.propertyTax + inputs.insurance +
            inputs.maintenance + managementFee + monthlyMortgage;

        // Effective monthly rent (accounting for vacancy)
        const effectiveRent = inputs.monthlyRent * (1 - inputs.vacancy / 100);

        // Monthly cash flow
        const monthlyCashFlow = effectiveRent - totalMonthlyExpenses;
        const annualCashFlow = monthlyCashFlow * 12;

        // Annual ROI
        const totalInvestment = inputs.downPayment;
        const annualROI = (annualCashFlow / totalInvestment) * 100;

        // Cap Rate
        const annualIncome = inputs.monthlyRent * 12;
        const annualOperatingExpenses = (inputs.propertyTax + inputs.insurance +
            inputs.maintenance + managementFee) * 12;
        const NOI = annualIncome - annualOperatingExpenses;
        const capRate = (NOI / inputs.purchasePrice) * 100;

        // Multi-year projections
        const projections = [];
        let currentValue = inputs.purchasePrice;
        let totalEquity = inputs.downPayment;
        let remainingLoan = loanAmount;

        for (let year = 1; year <= 15; year++) {
            // Property appreciation
            currentValue = currentValue * (1 + inputs.appreciation / 100);

            // Principal paid this year
            const principalPaid = (monthlyMortgage * 12) - (remainingLoan * (inputs.interestRate / 100));
            remainingLoan = Math.max(0, remainingLoan - principalPaid);

            // Total equity
            totalEquity = currentValue - remainingLoan;

            // Cumulative cash flow
            const cumulativeCashFlow = annualCashFlow * year;

            // Total return
            const totalReturn = totalEquity + cumulativeCashFlow - inputs.downPayment;
            const totalROI = (totalReturn / inputs.downPayment) * 100;

            projections.push({
                year,
                propertyValue: Math.round(currentValue),
                equity: Math.round(totalEquity),
                cumulativeCashFlow: Math.round(cumulativeCashFlow),
                totalReturn: Math.round(totalReturn),
                roi: totalROI.toFixed(1)
            });
        }

        setResults({
            monthlyMortgage,
            monthlyCashFlow,
            annualCashFlow,
            annualROI,
            capRate,
            NOI,
            projections,
            year5: projections[4],
            year10: projections[9],
            year15: projections[14]
        });
    };

    const handleInputChange = (field, value) => {
        setInputs(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
    };

    const formatCurrency = (value) => `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Calculator className="w-6 h-6 text-primary" />
                        Calculadora de Rentabilidad (ROI)
                    </CardTitle>
                    <CardDescription>
                        Analiza el retorno de inversión de una propiedad con proyecciones a 5, 10 y 15 años.
                        Esta herramienta te ayuda a tomar decisiones informadas sobre inversiones inmobiliarias.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    {/* Instructivo */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                            <span className="text-lg">💡</span> Cómo usar esta calculadora:
                        </h3>
                        <ul className="text-sm text-blue-800 space-y-1 ml-6 list-disc">
                            <li>Ingresa los datos de la propiedad que deseas analizar</li>
                            <li>Puedes dejar campos en blanco si no aplican (se tomarán como $0)</li>
                            <li>La calculadora te mostrará si la inversión es rentable</li>
                            <li>Revisa las proyecciones a 5, 10 y 15 años para ver el crecimiento</li>
                        </ul>
                    </div>

                    {/* Input Form */}
                    <div className="space-y-6">
                        {/* Datos de Compra */}
                        <div className="border-l-4 border-primary pl-4">
                            <h3 className="font-bold text-lg text-gray-800 mb-3">📋 Datos de Compra</h3>
                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="purchasePrice" className="flex items-center gap-1">
                                        Precio de Compra
                                        <span className="text-xs text-gray-500">(Valor total de la propiedad)</span>
                                    </Label>
                                    <Input
                                        id="purchasePrice"
                                        type="number"
                                        placeholder="150000"
                                        value={inputs.purchasePrice}
                                        onChange={(e) => handleInputChange('purchasePrice', e.target.value)}
                                        onFocus={handleFocus}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="downPayment" className="flex items-center gap-1">
                                        Enganche / Inversión Inicial
                                        <span className="text-xs text-gray-500">(Lo que pagarás de contado)</span>
                                    </Label>
                                    <Input
                                        id="downPayment"
                                        type="number"
                                        placeholder="30000"
                                        value={inputs.downPayment}
                                        onChange={(e) => handleInputChange('downPayment', e.target.value)}
                                        onFocus={handleFocus}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Datos del Préstamo */}
                        <div className="border-l-4 border-amber-500 pl-4">
                            <h3 className="font-bold text-lg text-gray-800 mb-3">🏦 Datos del Préstamo</h3>
                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="interestRate" className="flex items-center gap-1">
                                        Tasa de Interés Anual (%)
                                        <span className="text-xs text-gray-500">(Ej: 7.5)</span>
                                    </Label>
                                    <Input
                                        id="interestRate"
                                        type="number"
                                        step="0.01"
                                        placeholder="7.5"
                                        value={inputs.interestRate}
                                        onChange={(e) => handleInputChange('interestRate', e.target.value)}
                                        onFocus={handleFocus}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="loanTerm" className="flex items-center gap-1">
                                        Plazo del Préstamo (años)
                                        <span className="text-xs text-gray-500">(Ej: 20)</span>
                                    </Label>
                                    <Input
                                        id="loanTerm"
                                        type="number"
                                        placeholder="20"
                                        value={inputs.loanTerm}
                                        onChange={(e) => handleInputChange('loanTerm', e.target.value)}
                                        onFocus={handleFocus}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Ingresos */}
                        <div className="border-l-4 border-green-500 pl-4">
                            <h3 className="font-bold text-lg text-gray-800 mb-3">💰 Ingresos</h3>
                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="monthlyRent" className="flex items-center gap-1">
                                        Renta Mensual Esperada
                                        <span className="text-xs text-gray-500">(¿Cuánto cobrarás?)</span>
                                    </Label>
                                    <Input
                                        id="monthlyRent"
                                        type="number"
                                        placeholder="800"
                                        value={inputs.monthlyRent}
                                        onChange={(e) => handleInputChange('monthlyRent', e.target.value)}
                                        onFocus={handleFocus}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="appreciation" className="flex items-center gap-1">
                                        Apreciación Anual (%)
                                        <span className="text-xs text-gray-500">(Cuánto sube el valor, típico 3-5%)</span>
                                    </Label>
                                    <Input
                                        id="appreciation"
                                        type="number"
                                        step="0.1"
                                        placeholder="3"
                                        value={inputs.appreciation}
                                        onChange={(e) => handleInputChange('appreciation', e.target.value)}
                                        onFocus={handleFocus}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Gastos */}
                        <div className="border-l-4 border-red-500 pl-4">
                            <h3 className="font-bold text-lg text-gray-800 mb-3">📉 Gastos Mensuales</h3>
                            <div className="grid md:grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="propertyTax" className="flex items-center gap-1">
                                        Impuesto Municipal
                                        <span className="text-xs text-gray-500">(Mensual)</span>
                                    </Label>
                                    <Input
                                        id="propertyTax"
                                        type="number"
                                        placeholder="150"
                                        value={inputs.propertyTax}
                                        onChange={(e) => handleInputChange('propertyTax', e.target.value)}
                                        onFocus={handleFocus}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="insurance" className="flex items-center gap-1">
                                        Seguro
                                        <span className="text-xs text-gray-500">(Mensual)</span>
                                    </Label>
                                    <Input
                                        id="insurance"
                                        type="number"
                                        placeholder="80"
                                        value={inputs.insurance}
                                        onChange={(e) => handleInputChange('insurance', e.target.value)}
                                        onFocus={handleFocus}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="maintenance" className="flex items-center gap-1">
                                        Mantenimiento
                                        <span className="text-xs text-gray-500">(Reparaciones, etc.)</span>
                                    </Label>
                                    <Input
                                        id="maintenance"
                                        type="number"
                                        placeholder="100"
                                        value={inputs.maintenance}
                                        onChange={(e) => handleInputChange('maintenance', e.target.value)}
                                        onFocus={handleFocus}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="management" className="flex items-center gap-1">
                                        Cuota Administración (%)
                                        <span className="text-xs text-gray-500">(Si usas administradora)</span>
                                    </Label>
                                    <Input
                                        id="management"
                                        type="number"
                                        step="0.1"
                                        placeholder="10"
                                        value={inputs.management}
                                        onChange={(e) => handleInputChange('management', e.target.value)}
                                        onFocus={handleFocus}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="vacancy" className="flex items-center gap-1">
                                        Vacancia (%)
                                        <span className="text-xs text-gray-500">(Meses sin inquilino)</span>
                                    </Label>
                                    <Input
                                        id="vacancy"
                                        type="number"
                                        step="0.1"
                                        placeholder="5"
                                        value={inputs.vacancy}
                                        onChange={(e) => handleInputChange('vacancy', e.target.value)}
                                        onFocus={handleFocus}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <Button onClick={calculate} size="lg" className="w-full">
                        <Calculator className="mr-2 h-5 w-5" />
                        Calcular Rentabilidad
                    </Button>
                </CardContent>
            </Card>

            {/* Results */}
            {results && (
                <>
                    {/* Key Metrics */}
                    <div className="grid md:grid-cols-4 gap-4">
                        <Card>
                            <CardContent className="pt-6">
                                <div className="text-center">
                                    <DollarSign className="w-8 h-8 text-green-600 mx-auto mb-2" />
                                    <p className="text-sm text-gray-600">Flujo de Caja Mensual</p>
                                    <p className={`text-2xl font-bold ${results.monthlyCashFlow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {formatCurrency(results.monthlyCashFlow)}
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="pt-6">
                                <div className="text-center">
                                    <Percent className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                                    <p className="text-sm text-gray-600">ROI Anual</p>
                                    <p className="text-2xl font-bold text-blue-600">
                                        {results.annualROI.toFixed(2)}%
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="pt-6">
                                <div className="text-center">
                                    <TrendingUp className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                                    <p className="text-sm text-gray-600">Cap Rate</p>
                                    <p className="text-2xl font-bold text-purple-600">
                                        {results.capRate.toFixed(2)}%
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="pt-6">
                                <div className="text-center">
                                    <Calendar className="w-8 h-8 text-amber-600 mx-auto mb-2" />
                                    <p className="text-sm text-gray-600">NOI Anual</p>
                                    <p className="text-2xl font-bold text-amber-600">
                                        {formatCurrency(results.NOI)}
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Multi-Year Projections */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Proyecciones a Largo Plazo</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid md:grid-cols-3 gap-4 mb-6">
                                <div className="bg-blue-50 p-4 rounded-lg">
                                    <p className="text-sm font-semibold text-blue-900 mb-2">5 Años</p>
                                    <p className="text-xs text-gray-600">Valor Propiedad: {formatCurrency(results.year5.propertyValue)}</p>
                                    <p className="text-xs text-gray-600">Equity: {formatCurrency(results.year5.equity)}</p>
                                    <p className="text-xs text-gray-600">Flujo Acumulado: {formatCurrency(results.year5.cumulativeCashFlow)}</p>
                                    <p className="text-lg font-bold text-blue-600 mt-2">ROI: {results.year5.roi}%</p>
                                </div>
                                <div className="bg-green-50 p-4 rounded-lg">
                                    <p className="text-sm font-semibold text-green-900 mb-2">10 Años</p>
                                    <p className="text-xs text-gray-600">Valor Propiedad: {formatCurrency(results.year10.propertyValue)}</p>
                                    <p className="text-xs text-gray-600">Equity: {formatCurrency(results.year10.equity)}</p>
                                    <p className="text-xs text-gray-600">Flujo Acumulado: {formatCurrency(results.year10.cumulativeCashFlow)}</p>
                                    <p className="text-lg font-bold text-green-600 mt-2">ROI: {results.year10.roi}%</p>
                                </div>
                                <div className="bg-purple-50 p-4 rounded-lg">
                                    <p className="text-sm font-semibold text-purple-900 mb-2">15 Años</p>
                                    <p className="text-xs text-gray-600">Valor Propiedad: {formatCurrency(results.year15.propertyValue)}</p>
                                    <p className="text-xs text-gray-600">Equity: {formatCurrency(results.year15.equity)}</p>
                                    <p className="text-xs text-gray-600">Flujo Acumulado: {formatCurrency(results.year15.cumulativeCashFlow)}</p>
                                    <p className="text-lg font-bold text-purple-600 mt-2">ROI: {results.year15.roi}%</p>
                                </div>
                            </div>

                            {/* Chart */}
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={results.projections}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="year" label={{ value: 'Años', position: 'insideBottom', offset: -5 }} />
                                    <YAxis />
                                    <Tooltip formatter={(value) => formatCurrency(value)} />
                                    <Legend />
                                    <Line type="monotone" dataKey="propertyValue" stroke="#3b82f6" name="Valor Propiedad" />
                                    <Line type="monotone" dataKey="equity" stroke="#10b981" name="Equity" />
                                    <Line type="monotone" dataKey="totalReturn" stroke="#8b5cf6" name="Retorno Total" />
                                </LineChart>
                            </ResponsiveContainer>
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
};

export default ROICalculator;
